// Landing — public marketing page at `/`.
//
// SENTRY NEXUS brand frame (dark navy with cyan accent), top bar
// with logo + "主控台" CTA that bounces signed-in users to /dashboard
// and unsigned users to /login. Hero + 3-column feature grid +
// footer.

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogIn,
  ShieldCheck,
  Radio,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { hasSessionHint } from "../../lib/auth.js";
import HeroBuilding from "../../landing/HeroBuilding.js";

export default function LandingHome(): React.ReactElement {
  const navigate = useNavigate();

  const onConsoleClick = () => {
    // Cheap synchronous check via hint cookie. /dashboard itself does
    // a real /v1/auth/me round trip so a stale cookie is harmless.
    if (hasSessionHint()) navigate("/dashboard/");
    else navigate("/login?return_to=/dashboard/");
  };

  return (
    <div className="min-h-screen w-full bg-bg-base text-text-primary flex flex-col">
      <TopBar onConsoleClick={onConsoleClick} />
      <Hero onConsoleClick={onConsoleClick} />
      <Features />
      <Footer />
    </div>
  );
}

// ── Top bar ─────────────────────────────────────────────────────────────

function TopBar({ onConsoleClick }: { onConsoleClick: () => void }): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-30 w-full border-b border-border backdrop-blur"
      style={{ background: "rgba(10, 25, 47, 0.78)" }}
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <img src="/sentry-logo.png" alt="SENTRY" className="h-6 w-auto opacity-90" />
          <span className="text-sm font-semibold tracking-wide text-text-primary">
            NEXUS
          </span>
        </Link>
        <button onClick={onConsoleClick} className="btn-primary px-4">
          <LogIn size={16} />
          主控台
        </button>
      </div>
    </header>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────

function Hero({ onConsoleClick }: { onConsoleClick: () => void }): React.ReactElement {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #081420 0%, #0d2030 50%, #061018 100%)",
      }}
    >
      {/* blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,212,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.05) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse at 50% 30%, black 50%, transparent 80%)",
        }}
      />
      {/* glow */}
      <div
        aria-hidden
        className="absolute pointer-events-none top-[-120px] left-1/2 -translate-x-1/2"
        style={{
          width: 720,
          height: 720,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,212,255,0.16) 0%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      <div className="relative mx-auto max-w-6xl px-4 md:px-6 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
        <div className="flex flex-col gap-5">
          <span
            className="inline-flex w-fit items-center rounded-full px-3 py-1 text-2xs uppercase tracking-wider"
            style={{
              background: "rgba(0,212,255,0.10)",
              border: "1px solid rgba(0,212,255,0.25)",
              color: "#7be8ff",
            }}
          >
            SENTRY · AI EOS
          </span>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
            你的工作，一個 AI 入口 <br className="hidden md:block" />
            <span className="text-accent">有系統就整合，沒系統就生成</span>
          </h1>
          <p className="text-base md:text-lg text-text-secondary max-w-xl leading-relaxed">
            NEXUS 聚合知識、聯絡人、專案、任務、行程與收支，
            讓 AI 理解你的完整工作情境並擬出執行計畫——
            經你確認後才執行，每一步都留下稽核紀錄。
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <button onClick={onConsoleClick} className="btn-primary px-5 py-2.5 text-base">
              立即試用
              <ArrowRight size={18} />
            </button>
            <Link to="/register" className="btn-secondary px-5 py-2.5 text-base no-underline">
              建立帳號
            </Link>
          </div>
        </div>
        <div className="flex items-center justify-center md:justify-end">
          {/*
            HeroBuilding — exploded isometric 5-floor patrol-tower SVG.
            Floor 3 is enlarged and carries breathing patrol indicators
            (2 green normal + 1 red alert). On mobile it stacks below
            the headline copy and scales down to ~320 px without losing
            the geometry.
          */}
          <HeroBuilding />
        </div>
      </div>
    </section>
  );
}

// ── Features ────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: MessageCircle,
    title: "統一智慧入口",
    body: "對話或傳統介面皆可：NEXUS 解析你目前的情境，聚合知識、工作、行程、聯絡人與外部系統，跨來源理解與回答。",
  },
  {
    icon: ShieldCheck,
    title: "AI 提案、你核准",
    body: "AI 產生建議與執行計畫（步驟、來源、影響、風險）；具副作用的操作一律先取得你的明確確認，才交由確定性 Runtime 執行並留下稽核。",
  },
  {
    icon: Radio,
    title: "有系統就整合，沒系統就生成",
    body: "既有工具（日曆、Email、Notion、試算表…）直接連接；還沒有系統的流程，用可組合的 Pattern 生成——不是縮小版 ERP，也不是聊天機器人。",
  },
];

function Features(): React.ReactElement {
  return (
    <section className="bg-bg-base py-16 md:py-24 border-y border-border">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">一個入口，三個原則</h2>
          <p className="mt-3 text-text-secondary">AI 提案、你核准、確定性執行——全平台跑在 Cloudflare 邊緣。</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} icon={<f.icon size={20} />} title={f.title} body={f.body} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  body: string;
}

function FeatureCard({ icon, title, body }: FeatureCardProps): React.ReactElement {
  return (
    <article className="card p-6 hover:bg-bg-hover transition-colors">
      <div
        className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md"
        style={{
          background: "rgba(0,212,255,0.10)",
          color: "#00d4ff",
        }}
      >
        {icon}
      </div>
      <h3 className="text-base font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-sm text-text-secondary leading-relaxed">{body}</p>
    </article>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────

function Footer(): React.ReactElement {
  return (
    <footer className="bg-bg-base text-text-muted">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <img src="/sentry-logo.png" alt="SENTRY" className="h-5 w-auto opacity-70" />
          <span className="text-xs">© {new Date().getFullYear()} SENTRY NEXUS</span>
        </div>
        <nav className="flex items-center gap-5 text-xs">
          <Link to="/privacy" className="hover:text-text-secondary transition-colors no-underline">
            隱私政策
          </Link>
          <Link to="/terms" className="hover:text-text-secondary transition-colors no-underline">
            服務條款
          </Link>
        </nav>
      </div>
    </footer>
  );
}
